import { Link, Table, TableBody, TableCell, TableContainer, TableRow, Typography } from '@mui/material';

import { I18N_DE } from '@/i18n/de';
import type { ParkAndRideFacility, ParkingFacility } from '@/modules/api/backend';

interface ParkAndRideFacilityDetailCardTableProps {
  facility: ParkingFacility | ParkAndRideFacility;
}

function formatAddress(
  streetName: string | null,
  houseNumber: string | null,
  postalCode: string | null,
  city: string | null,
) {
  return [[streetName, houseNumber].filter(Boolean).join(' '), [postalCode, city].filter(Boolean).join(' ')]
    .filter(Boolean)
    .join(', ');
}

export function ParkingFacilityDetailTable({ facility }: ParkAndRideFacilityDetailCardTableProps) {
  const rows = [
    {
      label: 'Zufahrt',
      value: formatAddress(facility.entranceStreet, facility.entranceHouseNumber, facility.postalCode, facility.city),
    },
    ...(facility.entranceSecondaryStreet
      ? [
          {
            label: 'Zufahrt 2',
            value: formatAddress(
              facility.entranceSecondaryStreet,
              facility.entranceSecondaryHouseNumber,
              facility.postalCode,
              facility.city,
            ),
          },
        ]
      : []),
    ...('prStatusNote' in facility ? [{ label: 'Hinweis', value: facility.prStatusNote }] : []),
    {
      label: 'Art der Bewirtschaftung',
      value: facility.typeOfManagement ? I18N_DE.TypeOfManagement[facility.typeOfManagement] : undefined,
    },
    ...(facility.entranceHeight || facility.entranceWidth
      ? [
          {
            label: 'Einfahrt',
            value: [
              facility.entranceHeight && `Höhe: ${facility.entranceHeight.toFixed(2)} m`,
              facility.entranceWidth && `Breite: ${facility.entranceWidth.toFixed(2)} m`,
            ]
              .filter(Boolean)
              .join('\n'),
          },
        ]
      : []),
    {
      label: 'Verbot für Fahrzeugtypen',
      value:
        Array.isArray(facility.entranceProhibited) && facility.entranceProhibited.length > 0
          ? facility.entranceProhibited
          : undefined,
    },
    {
      label: 'Oberfläche',
      value: facility.parkingSurface ? I18N_DE.ParkingSurface[facility.parkingSurface] : undefined,
    },
    {
      label: 'Öffnungszeiten',
      value: facility.isAlwaysOpen ? '24 h geöffnet' : facility.openingHours,
    },
    ...(facility.freeToUse || facility.managementUrl
      ? [
          {
            label: 'Kosten',
            value: facility.freeToUse ? (
              'Kostenfrei Nutzbar'
            ) : (
              <Link
                href={facility.managementUrl ?? ''}
                rel="noopener noreferrer"
                sx={{ wordBreak: 'break-all' }}
                target="_blank"
              >
                {new URL(facility.managementUrl ?? '').hostname}
              </Link>
            ),
          },
        ]
      : []),

    {
      label: 'Parkdauer',
      value: facility.isParkingUnlimited ? 'Parkdauer unbegrenzt' : facility.parkingDuration,
    },

    ...(facility.usable !== undefined && facility.usable !== null
      ? [
          {
            label: 'Anlage nutzbar',
            value: facility.usable ? 'Ja' : 'Nein',
          },
        ]
      : []),

    {
      label: 'Betreiber',
      value: facility.organization?.url ? (
        <Link href={facility.organization.url} rel="noopener noreferrer" target="_blank">
          {facility.organization.name}
        </Link>
      ) : (
        facility.organization?.name
      ),
    },
  ];

  const visibleRows = rows.filter((row) => !!row.value);

  return (
    <TableContainer>
      <Table>
        <TableBody>
          {visibleRows.map((row) => (
            <TableRow key={row.label}>
              <TableCell sx={{ wordBreak: 'break-word', hyphens: 'auto', width: 120 }}>
                <Typography lang="de" variant="caption">
                  {row.label}
                </Typography>
              </TableCell>
              <TableCell sx={{ whiteSpace: 'pre-line' }}>
                <Typography variant="body2">{row.value}</Typography>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
